/**
 * Media Quality Policy Schemas
 *
 * Per-school media quality caps resolved server-side and served to mobile via
 * the app-config `globalFeatures` channel (key: `media-quality`). The chokepoint
 * upload engine reads these caps to normalize photos and bound video encodes.
 *
 * Two tiers are defined:
 *  - `standard` — the active default for every school (no override needed)
 *  - `pro`      — defined but dormant (no purchase path this launch); the
 *                 resolver is the seam where a future entitlement-grant check
 *                 plugs in.
 *
 * Zero schema migration: the tier selector lives in `SystemConfiguration`
 * (MEDIA_CONFIG) and the caps-per-tier table lives in backend code. A school
 * with no override resolves to `standard`.
 *
 * Single source of truth: the backend injects it, mobile's upload engine parses
 * it at runtime, and the app-config `globalFeatures` schema imports it here — no
 * hand-synced mirror (the former mobile copy was retired in SCH-693).
 */

import { z } from 'zod'

/**
 * Media quality tier identifier.
 * `standard` is the active default; `pro` is dormant (no purchase path yet).
 */
export const MediaQualityTierSchema = z.enum(['standard', 'pro'])
export type MediaQualityTier = z.infer<typeof MediaQualityTierSchema>

/**
 * Photo normalization caps applied by the mobile chokepoint at upload time.
 * - `maxDimension` — long-edge resize target in pixels (never upscale)
 * - `quality` — JPEG re-encode quality (1–100)
 */
export const MediaPhotoCapsSchema = z.object({
  maxDimension: z.number().int().positive(),
  quality: z.number().int().min(1).max(100)
})
export type MediaPhotoCaps = z.infer<typeof MediaPhotoCapsSchema>

/**
 * MUX max-resolution tier (maps to MUX `new_asset_settings.max_resolution_tier`).
 */
export const MediaVideoResolutionSchema = z.enum(['1080p', '1440p', '2160p'])
export type MediaVideoResolution = z.infer<typeof MediaVideoResolutionSchema>

/**
 * MUX encode quality (maps to MUX `new_asset_settings.video_quality`;
 * `plus` is the renamed Smart encoding tier).
 */
export const MediaVideoQualitySchema = z.enum(['basic', 'plus'])
export type MediaVideoQuality = z.infer<typeof MediaVideoQualitySchema>

/**
 * Video encode caps applied by the mobile chokepoint at upload time.
 * - `maxResolution` — MUX max-resolution tier ceiling
 * - `quality` — MUX encode quality
 * - `maxDurationSeconds` — pre-upload duration ceiling (advisory cap)
 */
export const MediaVideoCapsSchema = z.object({
  maxResolution: MediaVideoResolutionSchema,
  quality: MediaVideoQualitySchema,
  maxDurationSeconds: z.number().int().positive()
})
export type MediaVideoCaps = z.infer<typeof MediaVideoCapsSchema>

/**
 * The full resolved media-quality policy block served into `globalFeatures`.
 * `tier` is the resolved selector; `photo`/`video` are the caps for that tier.
 */
export const MediaQualityPolicySchema = z.object({
  tier: MediaQualityTierSchema,
  photo: MediaPhotoCapsSchema,
  video: MediaVideoCapsSchema
})
export type MediaQualityPolicy = z.infer<typeof MediaQualityPolicySchema>
